import RichEditor from "@common/components/Inputs/RichEditor";
import { getTooltipAttributes } from "@common/contexts/Tooltip";
import { type ButtonProps } from "@common/controls/Button/component";
import Icon from "@common/controls/Icon/component";
import { type ModalProps } from "@common/controls/Modal/component";
import { IconType, type WithChildren } from "@common/declarations";
import { useI18n } from "@common/hooks/i18n";
import classNames from "classnames";
import { identity } from "lodash";
import * as React from "react";
import { type Courses } from "../types";
import Media from "./Media";
import Quiz from "./Quiz";

export type Props = {
  ButtonComponent?: React.ComponentType<ButtonProps>;
  ConfirmModalComponent?: React.ComponentType<ModalProps>;
  courseId: string;
  data: Courses.CourseLessonAppState | null;
  doesNotRequireVideoApp?: boolean;
  enter: (courseId: string, lessonId: string) => void;
  leave: (courseId: string, lessonId: string) => void;
  lessonId: string | null;
  nextLessonId: string | null;
  onArrowClick: (lessonId: string) => void;
  previousLessonId: string | null;
  quizBack: (lessonId: string) => void;
  quizProceed: (lessonId: string, questionId: string, answerIds: string[]) => void;
  quizReview: (lessonId: string) => void;
  quizReviewEnd: (lessonId: string) => void;
  quizStart: (lessonId: string) => void;
  quizInfoDisplayMode?: "oneline" | "multiline";

  bodyContainerRef?: React.RefObject<HTMLDivElement | null>;
  bodyRef?: React.RefObject<HTMLDivElement | null>;
};

export const LessonBody = (props: Props) => {
  const i18n = useI18n();

  React.useEffect(() => {
    props.lessonId && props.enter(props.courseId, props.lessonId);

    return () => {
      props.lessonId && props.leave(props.courseId, props.lessonId);
    };
  }, [props.courseId, props.lessonId, props.enter, props.leave]);

  const onLeftArrowClick = React.useCallback(
    () => props.previousLessonId && props.onArrowClick(props.previousLessonId),
    [props.previousLessonId]
  );

  const onRightArrowClick = React.useCallback(
    () => props.nextLessonId && props.onArrowClick(props.nextLessonId),
    [props.nextLessonId]
  );

  if (!props.lessonId || !props.data) return null;

  const { programModuleLessonTypeKey: typeKey } = props.data.lesson;

  return (
    <div ref={props.bodyContainerRef} className="body_container">
      {!!props.previousLessonId && (
        <div className="arrow_left" onClick={onLeftArrowClick} {...getTooltipAttributes(i18n.previousLesson)}>
          <Icon icon={IconType.AngleLeft} />
        </div>
      )}

      {!!props.nextLessonId && (
        <div className="arrow_right" onClick={onRightArrowClick} {...getTooltipAttributes(i18n.nextLesson)}>
          <Icon icon={IconType.AngleRight} />
        </div>
      )}

      <div ref={props.bodyRef} className="body">
        {(typeKey === "Audio" || typeKey === "Video") && (
          <Media doesNotRequireVideoApp={props.doesNotRequireVideoApp} media={props.data.previewMedia} type={typeKey} />
        )}

        {typeKey === "Mixed" && (
          <RichEditor isInline isReadonly onChange={identity} value={props.data.lesson.contentHtml} />
        )}

        {typeKey === "Quiz" && (
          <Quiz
            answer={(questionId, answerIds) => props.quizProceed(props.lessonId!, questionId, answerIds)}
            back={() => props.quizBack(props.lessonId!)}
            ButtonComponent={props.ButtonComponent}
            ConfirmModalComponent={props.ConfirmModalComponent}
            infoDisplayMode={props.quizInfoDisplayMode}
            lesson={props.data}
            review={() => props.quizReview(props.lessonId!)}
            reviewEnd={() => props.quizReviewEnd(props.lessonId!)}
            start={() => props.quizStart(props.lessonId!)}
          />
        )}
      </div>
    </div>
  );
};

type LessonProps = Props &
  Partial<WithChildren> & {
    ref?: React.RefObject<HTMLDivElement | null>;
  };
const Lesson = ({ ref, children, ...props }: LessonProps) => (
  <div ref={ref} className={classNames("lesson", props.data?.lesson.programModuleLessonTypeKey.toLowerCase())}>
    <LessonBody {...props} />

    {children}
  </div>
);
export default Lesson;
